import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Page } from '../lib/api';
import { dateTime, mobile as fmtMobile, num } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Badge, Button, Card, CardHead, Chips, Field, Input, Modal, Notice, SearchInput, Textarea, useToast } from '../components/ui';
import type { BadgeTone } from '../components/ui';

export interface PersonLite {
  id: string;
  displayName: string | null;
  mobile: string;
}

interface PushStatus {
  provider: string;
  connected: boolean;
  devices: number;
  people: number;
  lastRegisteredAt: string | null;
  last24h: { sends: number; delivered: number; failed: number };
}

type CampaignStatus = 'DRAFT' | 'SCHEDULED' | 'SENDING' | 'SENT' | 'FAILED' | 'CANCELLED';

interface CampaignRow {
  id: string;
  title: string;
  body: string;
  status: CampaignStatus;
  isTest: boolean;
  target:
    | { kind: 'EVERYONE' }
    | { kind: 'GROUP'; group: { id: string; name: string } }
    | { kind: 'PEOPLE'; count: number; sample: PersonLite[] };
  createdBy: PersonLite | null;
  createdAt: string;
  scheduledAt: string | null;
  startedAt: string | null;
  sentAt: string | null;
  cancelledAt: string | null;
  audience: number;
  devices: number;
  delivered: number;
  pushFailed: number;
  errors: Record<string, number>;
  lastError: string | null;
}

export interface SendResult {
  status: CampaignStatus;
  audience: number;
  devices: number;
  delivered: number;
  pushFailed: number;
  errors: Record<string, number>;
  lastError: string | null;
}

const STATUS_TONE: Record<CampaignStatus, BadgeTone> = {
  DRAFT: 'neutral',
  SCHEDULED: 'blue',
  SENDING: 'amber',
  SENT: 'green',
  FAILED: 'red',
  CANCELLED: 'neutral',
};

const STATUS_LABEL: Record<CampaignStatus, string> = {
  DRAFT: 'Not sent',
  SCHEDULED: 'Scheduled',
  SENDING: 'Sending',
  SENT: 'Sent',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
};

/** Firebase's codes, and ours, in words an operator can act on. */
const ERROR_TEXT: Record<string, string> = {
  NO_DEVICES: 'No phone registered - the person has not opened the updated app while signed in',
  NO_AUDIENCE: 'Nobody matched the audience',
  'messaging/registration-token-not-registered': 'App uninstalled or signed out on that phone (removed)',
  'messaging/invalid-registration-token': 'Phone token not valid (removed)',
  'messaging/invalid-argument': 'Firebase refused the message',
  'messaging/third-party-auth-error': 'Firebase credentials rejected',
  'app/invalid-credential': 'Firebase credentials rejected',
  'messaging/server-unavailable': 'Firebase unavailable - try again',
  'messaging/internal-error': 'Firebase error - try again',
  'messaging/quota-exceeded': 'Firebase sending limit reached',
};

export const errorText = (code: string) => ERROR_TEXT[code] ?? code;

/** Whether push can reach phones at all, before anybody sends anything. */
export function PushStatusCard() {
  const { data } = useQuery({
    queryKey: ['push-status'],
    queryFn: () => api.get<PushStatus>('/notifications/push-status'),
    refetchInterval: 60_000,
  });
  if (!data) return null;
  return (
    <Notice
      tone={data.connected ? 'info' : 'warn'}
      title={data.connected ? 'Firebase push is connected' : 'Push is not connected to Firebase'}
    >
      {data.connected ? (
        <>
          <strong>{num(data.devices)}</strong> phone{data.devices === 1 ? '' : 's'} of <strong>{num(data.people)}</strong>{' '}
          {data.people === 1 ? 'person' : 'people'} can receive notifications
          {data.lastRegisteredAt ? <> · last registered {dateTime(data.lastRegisteredAt)}</> : null}. Last 24 hours:{' '}
          {num(data.last24h.sends)} send{data.last24h.sends === 1 ? '' : 's'}, {num(data.last24h.delivered)} delivered,{' '}
          {num(data.last24h.failed)} failed.
        </>
      ) : (
        <>Notifications are saved to each person&apos;s in-app list, but nothing is sent to phones (sender: {data.provider}).</>
      )}
    </Notice>
  );
}

function Audience({ row }: { row: CampaignRow }) {
  const t = row.target;
  if (t.kind === 'EVERYONE') return <span>Everyone</span>;
  if (t.kind === 'GROUP') return <span>{t.group.name}</span>;
  const names = t.sample.map((p) => p.displayName ?? fmtMobile(p.mobile));
  const more = t.count - names.length;
  return (
    <span>
      {names.join(', ')}
      {more > 0 ? ` +${more}` : ''}
    </span>
  );
}

/** Every send: when, to whom, and how far it got. */
export function PushHistory({ onSendAgain }: { onSendAgain?: (v: { title: string; body: string }) => void }) {
  const { get, set } = useListParams();
  const status = get('pstatus');
  const tests = get('ptests');
  const toast = useToast();
  const qc = useQueryClient();

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'cancel' | 'send' | 'delete' }): Promise<unknown> =>
      action === 'delete'
        ? api.del<{ deleted: boolean }>(`/notifications/campaigns/${id}`)
        : api.post<SendResult | { cancelled: boolean }>(`/notifications/campaigns/${id}/${action}`),
    onSuccess: (_r, v) => {
      toast(v.action === 'cancel' ? 'Scheduled send cancelled' : v.action === 'delete' ? 'Removed from the history' : 'Sent', 'success');
      qc.invalidateQueries({ queryKey: ['push-history'] });
      qc.invalidateQueries({ queryKey: ['push-status'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<CampaignRow>> = [
    {
      key: 'msg',
      header: 'Notification',
      render: (r) => (
        <div style={{ maxWidth: 320 }}>
          <div className="cell-strong">
            {r.title} {r.isTest && <Badge tone="purple">Test</Badge>}
          </div>
          <div className="cell-muted cell-clip">{r.body}</div>
        </div>
      ),
    },
    {
      key: 'to',
      header: 'To',
      render: (r) => (
        <div>
          <Audience row={r} />
          {r.createdBy && <div className="cell-muted">by {r.createdBy.displayName ?? fmtMobile(r.createdBy.mobile)}</div>}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <Badge tone={STATUS_TONE[r.status]} dot>
          {STATUS_LABEL[r.status]}
        </Badge>
      ),
    },
    {
      key: 'when',
      header: 'When',
      render: (r) => (
        <div className="cell-muted">
          {r.sentAt ? (
            <>Sent {dateTime(r.sentAt)}</>
          ) : r.status === 'SCHEDULED' ? (
            <>For {dateTime(r.scheduledAt)}</>
          ) : r.status === 'SENDING' && r.startedAt ? (
            <>Sending since {dateTime(r.startedAt)}</>
          ) : r.cancelledAt ? (
            <>Cancelled {dateTime(r.cancelledAt)}</>
          ) : (
            <>Made {dateTime(r.createdAt)}</>
          )}
        </div>
      ),
    },
    {
      key: 'reach',
      header: 'Delivered',
      render: (r) =>
        r.status === 'SENT' || r.status === 'FAILED' ? (
          <div>
            <div className="cell-strong">
              {num(r.delivered)} / {num(r.devices)} phones
            </div>
            <div className="cell-muted">{num(r.audience)} people</div>
          </div>
        ) : (
          <span className="cell-muted">—</span>
        ),
    },
    {
      key: 'errors',
      header: 'Errors',
      render: (r) => {
        const entries = Object.entries(r.errors ?? {});
        if (!entries.length && !r.lastError) return <span className="cell-muted">—</span>;
        return (
          <div style={{ maxWidth: 280 }}>
            {entries.length
              ? entries.map(([code, n]) => (
                  <div key={code} className="cell-muted">
                    {num(n)} × {errorText(code)}
                  </div>
                ))
              : r.lastError && <div className="cell-muted">{errorText(r.lastError)}</div>}
          </div>
        );
      },
    },
    {
      key: 'act',
      header: '',
      render: (r) => {
        // The spinner on the row being acted on, not on every row.
        const busy = (action: string) => act.isPending && act.variables?.id === r.id && act.variables.action === action;
        return (
          <div className="cell-actions">
            {r.status === 'SCHEDULED' && (
              <Button size="sm" variant="danger" loading={busy('cancel')} onClick={() => act.mutate({ id: r.id, action: 'cancel' })}>
                Cancel
              </Button>
            )}
            {(r.status === 'FAILED' || r.status === 'DRAFT') && (
              <Button size="sm" loading={busy('send')} onClick={() => act.mutate({ id: r.id, action: 'send' })}>
                {r.status === 'FAILED' ? 'Retry' : 'Send'}
              </Button>
            )}
            {onSendAgain && !r.isTest && (r.status === 'SENT' || r.status === 'CANCELLED') && (
              <Button size="sm" onClick={() => onSendAgain({ title: r.title, body: r.body })}>
                Send again
              </Button>
            )}
            {(r.status === 'DRAFT' || r.status === 'CANCELLED' || r.status === 'FAILED') && (
              <Button size="sm" variant="ghost" loading={busy('delete')} onClick={() => act.mutate({ id: r.id, action: 'delete' })}>
                Delete
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Card flush>
      <CardHead title="Notification history" sub="Every send, with how many phones it reached and why any did not" />
      <DataTable<CampaignRow>
        queryKey={['push-history', status, tests]}
        path="/notifications/campaigns"
        params={{ status, tests }}
        columns={columns}
        refetchInterval={15_000}
        emptyIcon="🔔"
        emptyTitle="Nothing sent yet"
        emptyBody="Sends, scheduled sends and test notifications appear here."
        toolbar={
          <div className="row" style={{ gap: 'var(--s3)', flexWrap: 'wrap' }}>
            <Chips
              value={status}
              onChange={(v) => set('pstatus', v)}
              options={[
                { value: '', label: 'All' },
                { value: 'DRAFT', label: 'Not sent' },
                { value: 'SCHEDULED', label: 'Scheduled' },
                { value: 'SENDING', label: 'Sending' },
                { value: 'SENT', label: 'Sent' },
                { value: 'FAILED', label: 'Failed' },
                { value: 'CANCELLED', label: 'Cancelled' },
              ]}
            />
            <Chips
              value={tests}
              onChange={(v) => set('ptests', v)}
              options={[
                { value: '', label: 'With tests' },
                { value: 'false', label: 'No tests' },
                { value: 'true', label: 'Tests only' },
              ]}
            />
          </div>
        }
      />
    </Card>
  );
}

interface SentRow {
  id: string;
  title: string;
  body: string;
  type: string;
  createdAt: string;
  readAt: string | null;
  clearedAt: string | null;
  user: PersonLite;
}

/** The kinds of notification people get, in words. */
const TYPE_LABEL: Record<string, string> = {
  ADMIN_PUSH: 'From the console',
  CHAT_MESSAGE: 'Chat message',
  CONTACT_REQUESTED: 'Contact request',
  CONTACT_APPROVED: 'Contact approved',
  CONTACT_REJECTED: 'Contact declined',
  CONTACT_REVEALED: 'Number shared',
  BOOKING_REQUESTED: 'Booking',
  GAP_EMPLOYEE_APPROVED: 'Employee approved',
  GAP_EMPLOYEE_REJECTED: 'Employee not approved',
  GENERAL: 'General',
};

/**
 * Every notification people were sent, not only the console's own sends:
 * messages, bookings, contact requests, partner news. Who got it, what it
 * said, its type, when, and whether they have read it.
 */
export function SentNotifications() {
  const { get, set } = useListParams();
  const type = get('ntype');
  const read = get('nread');
  const search = get('nsearch');
  const columns: Array<Column<SentRow>> = [
    {
      key: 'msg',
      header: 'Notification',
      render: (r) => (
        <div style={{ maxWidth: 320 }}>
          <div className="cell-strong">{r.title}</div>
          <div className="cell-muted cell-clip">{r.body}</div>
        </div>
      ),
    },
    {
      key: 'to',
      header: 'To',
      render: (r) => (
        <div>
          <div className="cell-strong">{r.user.displayName ?? 'Unnamed'}</div>
          <div className="cell-muted">{fmtMobile(r.user.mobile)}</div>
        </div>
      ),
    },
    { key: 'type', header: 'Type', render: (r) => <Badge tone={r.type === 'ADMIN_PUSH' ? 'blue' : 'neutral'}>{TYPE_LABEL[r.type] ?? r.type}</Badge> },
    { key: 'when', header: 'Date & time', render: (r) => <span className="cell-muted">{dateTime(r.createdAt)}</span> },
    {
      key: 'status',
      header: 'Status',
      render: (r) =>
        r.readAt ? (
          <div>
            <Badge tone="green" dot>
              Read
            </Badge>
            <div className="cell-muted">{dateTime(r.readAt)}</div>
          </div>
        ) : (
          <Badge tone="amber" dot>
            Unread
          </Badge>
        ),
    },
  ];
  return (
    <Card flush>
      <CardHead title="Sent to people" sub="Every notification in people's lists - from the console and from the app's own events" />
      <DataTable<SentRow>
        queryKey={['sent-notifications', type, read, search]}
        path="/notifications/sent"
        params={{ type, read, search }}
        columns={columns}
        emptyIcon="🔔"
        emptyTitle="No notifications"
        emptyBody="Notifications people receive appear here."
        toolbar={
          <div className="row" style={{ gap: 'var(--s3)', flexWrap: 'wrap' }}>
            <Chips
              value={type}
              onChange={(v) => set('ntype', v)}
              options={[
                { value: '', label: 'All types' },
                { value: 'ADMIN_PUSH', label: 'Console' },
                { value: 'CHAT_MESSAGE', label: 'Chat' },
                { value: 'CONTACT_REQUESTED', label: 'Contact' },
                { value: 'BOOKING_REQUESTED', label: 'Bookings' },
              ]}
            />
            <Chips
              value={read}
              onChange={(v) => set('nread', v)}
              options={[
                { value: '', label: 'Read or not' },
                { value: 'unread', label: 'Unread' },
                { value: 'read', label: 'Read' },
              ]}
            />
            <SearchInput value={search} onChange={(v) => set('nsearch', v)} placeholder="Name or mobile" />
          </div>
        }
      />
    </Card>
  );
}

/** What a send did, in a sentence or two. */
export function ResultSummary({ r }: { r: SendResult }) {
  const errors = Object.entries(r.errors ?? {});
  const tone = r.delivered > 0 ? 'info' : 'warn';
  return (
    <Notice
      tone={tone}
      title={
        r.devices === 0
          ? 'Saved in the app, but no phone to send to'
          : `Delivered to ${num(r.delivered)} of ${num(r.devices)} phone${r.devices === 1 ? '' : 's'}`
      }
    >
      {r.devices === 0 && r.lastError ? errorText(r.lastError) : null}
      {errors.map(([code, n]) => (
        <div key={code}>
          {num(n)} × {errorText(code)}
        </div>
      ))}
    </Notice>
  );
}

/** One notification to yourself or one number, straight away, to check push works. */
export function TestSender({ onClose }: { onClose: () => void }) {
  const [to, setTo] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const qc = useQueryClient();
  const toast = useToast();
  const send = useMutation({
    mutationFn: () =>
      api.post<SendResult & { to: PersonLite; phones: Array<{ platform: string; appVersion: string | null; updatedAt: string }> }>(
        '/notifications/test',
        {
          mobile: to.trim() || undefined,
          title: title.trim() || undefined,
          body: body.trim() || undefined,
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['push-history'] });
      qc.invalidateQueries({ queryKey: ['push-status'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const r = send.data;

  return (
    <Modal
      title="Send a test notification"
      sub="Goes out at once to one person's phones, and is kept in the history as a test."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button variant="primary" loading={send.isPending} onClick={() => send.mutate()}>
            Send test
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Send to" hint="Leave empty to send it to yourself">
          <Input value={to} inputMode="tel" maxLength={14} placeholder="Mobile number" onChange={(e) => setTo(e.target.value)} />
        </Field>
        <Field label="Title" hint="Optional">
          <Input value={title} maxLength={120} placeholder="Test notification" onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Message" hint="Optional">
          <Textarea
            value={body}
            maxLength={500}
            placeholder="If you can read this, push notifications reach this phone."
            onChange={(e) => setBody(e.target.value)}
          />
        </Field>
        {r && (
          <>
            <ResultSummary r={r} />
            <div className="cell-muted">
              To {r.to.displayName ?? 'Unnamed'} ({fmtMobile(r.to.mobile)}) ·{' '}
              {r.phones.length
                ? r.phones
                    .map((p) => `${p.platform}${p.appVersion ? ` v${p.appVersion}` : ''}, registered ${dateTime(p.updatedAt)}`)
                    .join(' · ')
                : 'no phone registered'}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/** Finds people by name or number and keeps the ones picked. */
export function PeoplePicker({ value, onChange }: { value: PersonLite[]; onChange: (v: PersonLite[]) => void }) {
  const [q, setQ] = useState('');
  const term = q.trim();
  const found = useQuery({
    queryKey: ['push-people', term],
    queryFn: () => api.get<Page<PersonLite>>(`/admin/users?limit=8&search=${encodeURIComponent(term)}`),
    enabled: term.length >= 3,
  });
  const picked = new Set(value.map((p) => p.id));

  return (
    <div className="stack" style={{ gap: 'var(--s2)' }}>
      {value.length > 0 && (
        <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
          {value.map((p) => (
            <button
              key={p.id}
              type="button"
              className="badge badge--blue"
              style={{ cursor: 'pointer', border: 0 }}
              title="Remove"
              onClick={() => onChange(value.filter((v) => v.id !== p.id))}
            >
              {p.displayName ?? fmtMobile(p.mobile)} ✕
            </button>
          ))}
        </div>
      )}
      <Input value={q} placeholder="Search by name or mobile (3+ characters)" onChange={(e) => setQ(e.target.value)} />
      {term.length >= 3 && (
        <div className="stack" style={{ gap: 2 }}>
          {found.isLoading ? (
            <div className="cell-muted">Searching…</div>
          ) : !found.data?.items.length ? (
            <div className="cell-muted">Nobody found</div>
          ) : (
            found.data.items.map((u) => (
              <button
                key={u.id}
                type="button"
                disabled={picked.has(u.id)}
                onClick={() => {
                  onChange([...value, { id: u.id, displayName: u.displayName, mobile: u.mobile }]);
                  setQ('');
                }}
                style={{
                  textAlign: 'left',
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid var(--line)',
                  background: picked.has(u.id) ? 'var(--bg)' : '#fff',
                  cursor: picked.has(u.id) ? 'default' : 'pointer',
                }}
              >
                <span className="cell-strong">{u.displayName ?? 'Unnamed'}</span>{' '}
                <span className="cell-muted">{fmtMobile(u.mobile)}</span>
                {picked.has(u.id) && <span className="cell-muted"> · added</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
